import { useState } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AppLayout from '@/components/feature/AppLayout';
import { useTheme } from '@/hooks/useTheme';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, Area, AreaChart,
} from 'recharts';
import { fetchStatistics, type Statistics } from '@/services/statistics';

function StatsContent() {
  const { theme, toggleTheme } = useTheme();
  const [timeRange, setTimeRange] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [stats, setStats] = useState<Statistics | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchStatistics().then((s) => {
      if (mounted && s) setStats(s);
    });
    return () => { mounted = false; };
  }, []);

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-background-100 border border-background-200/60 rounded-md p-3 shadow-sm">
        <p className="text-xs font-medium text-foreground-900 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-foreground-600">{entry.name}:</span>
            <span className="font-medium text-foreground-900">{entry.value}%</span>
          </div>
        ))}
      </div>
    );
  };

  const CustomBarTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-background-100 border border-background-200/60 rounded-md p-3 shadow-sm">
        <p className="text-xs font-medium text-foreground-900 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-foreground-600">{entry.name}:</span>
            <span className="font-medium text-foreground-900">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!stats) {
    return (
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        <header className="h-16 flex items-center px-4 md:px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
          <h2 className="font-display text-xl font-bold text-foreground-950">Statistics</h2>
        </header>
        <div className="flex-1 p-4 md:p-6 space-y-4 animate-pulse">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-24 rounded-lg bg-background-200/60" />
            ))}
          </div>
          <div className="h-64 rounded-lg bg-background-200/60" />
          <div className="h-64 rounded-lg bg-background-200/60" />
        </div>
      </div>
    );
  }

  const { streakData, completionStats, weeklyBreakdown, monthlyTrends, dayOfWeekStats, callStats, statsCards } = stats;

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
        <h2 className="font-display text-xl font-bold text-foreground-950">Statistics</h2>
        <div className="flex items-center gap-1">
          <button className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer" title="Notifications">
            <i className="ri-notification-3-line text-lg" />
          </button>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
            title="Toggle dark mode"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {/* Time range tabs */}
        <div className="flex items-center gap-1 mb-6 bg-background-100 rounded-full p-1 w-fit">
          {(['weekly', 'monthly', 'yearly'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition cursor-pointer whitespace-nowrap ${
                timeRange === range
                  ? 'bg-gold text-midnight shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {statsCards.map((card) => (
            <div key={card.label} className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
                  <i className={`${card.icon} ${card.color} text-sm`} />
                </span>
              </div>
              <p className="text-xl font-heading font-bold text-foreground-950">{card.value}</p>
              <p className="text-[11px] text-foreground-500 mt-0.5">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Streak banners */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="rounded-2xl p-5 border border-gold/20" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1e293b 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center">
                <i className="ri-fire-line text-gold text-xl flame-flicker" />
              </div>
              <div>
                <p className="font-display text-3xl font-bold text-white">{streakData.currentStreak} days</p>
                <p className="text-xs text-gold">Current Streak — since {streakData.currentStreakStart}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl p-5 border border-gold/20" style={{ background: 'linear-gradient(135deg, #0A0A0F 0%, #0F172A 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                <i className="ri-trophy-line text-gold text-xl" />
              </div>
              <div>
                <p className="font-display text-3xl font-bold text-white">{streakData.longestStreak} days</p>
                <p className="text-xs text-gold">Longest Streak — {streakData.longestStreakPeriod}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Monthly trend */}
          <div className="card p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">Monthly Completion Rate</h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={monthlyTrends}>
                <defs>
                  <linearGradient id="completionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4A843" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#D4A843" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} axisLine={false} tickLine={false} />
                <YAxis domain={[60, 100]} tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="completionRate"
                  name="Completion"
                  stroke="#D4A843"
                  fill="url(#completionGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Weekly breakdown */}
          <div className="card p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">Weekly Breakdown (Last 4 Weeks)</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={weeklyBreakdown} barSize={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="morningOnTime" name="AM On Time" stackId="morning" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="morningLate" name="AM Late" stackId="morning" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                <Bar dataKey="morningMissed" name="AM Missed" stackId="morning" fill="#f87171" radius={[4, 4, 0, 0]} />
                <Bar dataKey="skipped" name="Shabbat" stackId="skip" fill="#a78bfa" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bottom row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Day of week */}
          <div className="card p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">Success Rate by Day</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dayOfWeekStats} barSize={28} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" horizontal={true} vertical={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} axisLine={false} tickLine={false} unit="%" />
                <YAxis type="category" dataKey="day" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} axisLine={false} tickLine={false} width={35} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="successRate" name="Success Rate" fill="oklch(var(--accent-500))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Call stats */}
          <div className="card p-5">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">Call Statistics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-background-50 border border-background-200/60">
                <p className="text-2xl font-heading font-bold text-foreground-950">{callStats.totalCalls}</p>
                <p className="text-[11px] text-foreground-500">Total Calls</p>
              </div>
              <div className="p-4 rounded-lg bg-background-50 border border-background-200/60">
                <p className="text-2xl font-heading font-bold text-emerald-600">{callStats.answeredRate}%</p>
                <p className="text-[11px] text-foreground-500">Answer Rate</p>
              </div>
              <div className="p-4 rounded-lg bg-background-50 border border-background-200/60">
                <p className="text-2xl font-heading font-bold text-foreground-950">{callStats.avgDuration}s</p>
                <p className="text-[11px] text-foreground-500">Avg Duration</p>
              </div>
              <div className="p-4 rounded-lg bg-background-50 border border-background-200/60">
                <p className="text-2xl font-heading font-bold text-amber-600">{callStats.voicemailRate}%</p>
                <p className="text-[11px] text-foreground-500">Voicemail</p>
              </div>
            </div>

            {/* Mini progress bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-foreground-500">Answer Rate</span>
                <span className="text-[11px] font-medium text-foreground-800">{callStats.answeredRate}%</span>
              </div>
              <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${callStats.answeredRate}%` }}
                />
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-foreground-500">Voicemail</span>
                <span className="text-[11px] font-medium text-foreground-800">{callStats.voicemailRate}%</span>
              </div>
              <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{ width: `${callStats.voicemailRate}%` }}
                />
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-foreground-500">Missed Calls</span>
                <span className="text-[11px] font-medium text-foreground-800">{callStats.missedCallRate}%</span>
              </div>
              <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-red-400 transition-all"
                  style={{ width: `${callStats.missedCallRate}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Completion rate overview */}
        <div className="card p-5">
          <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">Completion Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/60">
              <div className="relative w-20 h-20 mx-auto mb-3">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="oklch(var(--background-300))" strokeWidth="8" />
                  <circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke="oklch(var(--accent-500))" strokeWidth="8"
                    strokeDasharray={`${completionStats.weekly * 2.136} 213.6`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-heading font-bold text-foreground-950">
                  {completionStats.weekly}%
                </span>
              </div>
              <p className="text-sm font-medium text-foreground-800">This Week</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/60">
              <div className="relative w-20 h-20 mx-auto mb-3">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="oklch(var(--background-300))" strokeWidth="8" />
                  <circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke="oklch(var(--primary-500))" strokeWidth="8"
                    strokeDasharray={`${completionStats.monthly * 2.136} 213.6`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-heading font-bold text-foreground-950">
                  {completionStats.monthly}%
                </span>
              </div>
              <p className="text-sm font-medium text-foreground-800">This Month</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/60">
              <div className="relative w-20 h-20 mx-auto mb-3">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="oklch(var(--background-300))" strokeWidth="8" />
                  <circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke="oklch(var(--secondary-500))" strokeWidth="8"
                    strokeDasharray={`${completionStats.yearly * 2.136} 213.6`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-heading font-bold text-foreground-950">
                  {completionStats.yearly}%
                </span>
              </div>
              <p className="text-sm font-medium text-foreground-800">This Year</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StatisticsPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <StatsContent />
      </AppLayout>
    </AuthGuard>
  );
}